import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { updatePageMeta, addStructuredData, addBreadcrumbSchema } from '../utils/seo';
import { metaFor, locationsListSchema, breadcrumbsFor } from '../seo/routeMeta';
import LOCATIONS from '../data/locations';
import { freeAreaPhrase } from '../data/faq';
import { getPricing } from '../utils/pricing';


/**
 * /locations — service-area index.
 *
 * WHY THIS EXISTS: the homepage carried a "View All Service Areas" CTA pointing
 * at /locations, but only /locations/:city was ever routed — so the button
 * returned the 404 page. A homepage CTA into a 404 costs conversions and hands
 * Google a broken internal link from the most-crawled page on the site.
 *
 * Data-driven from ../data/locations, same as LocationPage and the Footer:
 * adding a city there adds it here with no code change.
 *
 * Design deliberately matches LocationPage rather than introducing a new
 * treatment — dark gradient hero, Playfair display face, #c9a96e gold accent.
 * A hub page that looks like the pages it links to reads as one site.
 */
function LocationsIndex() {
  // Travel copy follows the live travel settings (2026-10-17).
  const [travel, setTravel] = useState(null);
  useEffect(() => {
    let cancelled = false;
    getPricing().then(d => { if (!cancelled && d && d.travel) setTravel(d.travel); }).catch(() => {});
    return () => { cancelled = true; };
  }, []);
  const freeArea = freeAreaPhrase(travel);

  useEffect(() => {
    window.scrollTo(0, 0);

    updatePageMeta(metaFor('/locations'));

    // ItemList of the city pages. Gives search and answer engines an explicit
    // map of coverage instead of making them infer it from scattered links.
    addStructuredData(locationsListSchema());
    addBreadcrumbSchema(breadcrumbsFor('/locations'));
  }, []);

  // Group by county so the page communicates coverage shape, not just a list.
  const byCounty = LOCATIONS.reduce((acc, loc) => {
    (acc[loc.county] = acc[loc.county] || []).push(loc);
    return acc;
  }, {});
  const counties = Object.keys(byCounty);

  return (
    <div style={{ paddingTop: '140px', minHeight: '60vh' }}>
      {/* Hero */}
      <section
        style={{
          background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
          color: 'white',
          padding: '60px 0',
          textAlign: 'center'
        }}
      >
        <div className="container">
          <p
            style={{
              textTransform: 'uppercase',
              letterSpacing: '0.15em',
              fontSize: '0.8rem',
              opacity: 0.7,
              marginBottom: '0.75rem'
            }}
          >
            Where we shoot
          </p>
          <h1 style={{ fontSize: '2.5rem', fontFamily: "'Playfair Display', serif", marginBottom: '0.5rem' }}>
            Service Areas
          </h1>
          <p style={{ fontSize: '1.2rem', opacity: 0.85 }}>
            Listing media across Utah County, Salt Lake, Summit, and Weber
          </p>
        </div>
      </section>

      {/* Body */}
      <section style={{ padding: '60px 0' }}>
        <div className="container" style={{ maxWidth: '900px', margin: '0 auto', padding: '0 1.5rem' }}>
          <p style={{ fontSize: '1.1rem', lineHeight: '1.8', color: '#374151', marginBottom: '2.5rem' }}>
            We're based in Eagle Mountain and shoot listings up and down the Wasatch Front. Every area below has
            its own page covering the neighborhoods we cover there and the media that tends to perform on local
            listings. Don't see your city? We travel{freeArea ? ` — travel is free ${freeArea} and quoted by distance beyond it` : ''}.
          </p>

          {counties.map((county) => (
            <div key={county} style={{ marginBottom: '2.5rem' }}>
              <h2
                style={{
                  fontSize: '1.5rem',
                  marginBottom: '1rem',
                  fontFamily: "'Playfair Display', serif",
                  borderBottom: '1px solid #e5e7eb',
                  paddingBottom: '0.5rem'
                }}
              >
                {county}
              </h2>

              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
                  gap: '1rem'
                }}
              >
                {byCounty[county].map((loc) => (
                  <Link
                    key={loc.slug}
                    to={`/locations/${loc.slug}`}
                    style={{
                      display: 'block',
                      textDecoration: 'none',
                      color: 'inherit',
                      background: '#f9fafb',
                      borderRadius: '12px',
                      padding: '20px',
                      borderLeft: '4px solid #c9a96e'
                    }}
                  >
                    <h3 style={{ margin: '0 0 6px', fontSize: '1.15rem', color: '#1a1a2e' }}>{loc.name}</h3>
                    <p style={{ margin: '0 0 10px', color: '#6b7280', fontSize: '0.95rem', lineHeight: 1.6 }}>
                      {loc.tagline}
                    </p>
                    <span style={{ color: '#2563eb', fontWeight: 600, fontSize: '0.9rem' }}>
                      View {loc.name} &rarr;
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {/* CTA */}
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginTop: '2.5rem' }}>
            <Link
              to="/book"
              style={{
                padding: '14px 32px',
                background: '#c9a96e',
                color: 'white',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '600'
              }}
            >
              Book a Shoot
            </Link>
            <Link
              to="/contact"
              style={{
                padding: '14px 32px',
                border: '2px solid #1a1a2e',
                color: '#1a1a2e',
                borderRadius: '6px',
                textDecoration: 'none',
                fontWeight: '500'
              }}
            >
              Ask About Your Area
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default LocationsIndex;
